import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/core/styles';
import Badge from '@material-ui/core/Badge';
import Grow from '@material-ui/core/Grow';
import IconButton from '@material-ui/core/IconButton';
import { ReactComponent as NotificationsIcon } from 'assets/icons/bell.svg';
import Typography from '@material-ui/core/Typography';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Divider from '@material-ui/core/Divider';
import { GET_UNREAD, MARK_ALL_READ, MARK_READ } from 'graphql/notification';
import { getURL } from 'utils/notifications';

const useStyles = makeStyles((theme) => ({
  root: {
    color: '#D2D7E3',
    marginRight: theme.spacing(2),
  },
  paper: {
    transformOrigin: 'top right',
  },
  notificationPaper: {
    zIndex: 100,
  },
  list: {
    width: theme.spacing(40),
    maxHeight: theme.spacing(40),
    overflow: 'auto',
  },
  listItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  header: {
    'fontSize': 12,
    'textAlign': 'right',
    'backgroundColor': theme.palette.background,
    'borderBottom': `1px solid ${theme.palette.divider}`,
    'padding': theme.spacing(1),
    '& a': {
      color: theme.palette.primary.main,
    },
  },
  footer: {
    'textAlign': 'center',
    'backgroundColor': theme.palette.background,
    'borderTop': `1px solid ${theme.palette.divider}`,
    'padding': theme.spacing(1),
    '& a': {
      color: theme.palette.primary.main,
    },
  },
}));

const NotificationMenu = ({ user }) => {
  const userId = user.type === 'admin' ? 0 : user.id;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const { data, refetch } = useQuery(GET_UNREAD, {
    variables: {
      userId,
    },
  });
  const [markReadAllMutation] = useMutation(MARK_ALL_READ, {
    variables: { userId },
    fetchPolicy: 'network-only',
  });
  const [markReadMutation] = useMutation(MARK_READ);
  const unseenNotificationsCount = data
    ? data.unread_aggregate.aggregate.count
    : 0;
  const messageList = data ? data.notifications : [];

  const handleToggle = () => {
    refetch();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleRead = (id) => {
    setOpen(false);
    markReadMutation({
      variables: {
        id,
      },
    }).then(() => refetch());
  };

  const markRead = (e) => {
    e.preventDefault();
    markReadMutation().then(() => refetch());
  };

  return (
    <div className={classes.root}>
      <IconButton ref={anchorRef} onClick={handleToggle}>
        <Badge variant="dot" color="secondary" badgeContent={unseenNotificationsCount}>
          <NotificationsIcon fontSize="small" color="action" />
        </Badge>
      </IconButton>
      <Popper
        id="notifications-popup"
        anchorEl={anchorRef.current}
        open={open}
        placement="bottom-end"
        transition
        role={undefined}
        className={classes.notificationPaper}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener
            onClickAway={() => {
              setOpen(false);
            }}
          >
            <Grow in={open} {...TransitionProps}>
              <Paper className={classes.paper}>
                <div className={classes.header}>
                  <a href="#" onClick={markRead}>
                    Mark all read
                  </a>
                </div>
                <List className={classes.list}>
                  {!messageList.length ? (
                    <Typography variant="body2" style={{ textAlign: 'center' }}>
                      No unread notifications
                    </Typography>
                  ) : null}
                  {messageList.map((message, index) => (
                    <React.Fragment key={message.id}>
                      <ListItem
                        alignItems="flex-start"
                        className={classes.listItem}
                      >
                        <Typography gutterBottom variant="body2">
                          <Link
                            to={getURL(message)}
                            onClick={() => handleRead(message.id)}
                          >
                            {' '}
                            {message.message}{' '}
                          </Link>{' '}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(message.created_at).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            },
                          )}
                        </Typography>
                      </ListItem>
                      {index < messageList.length - 1 ? (
                        <Divider className={classes.divider} />
                      ) : null}
                    </React.Fragment>
                  ))}
                </List>
                <div className={classes.footer}>
                  <Link to="/notifications" onClick={() => setOpen(false)}>
                    See All
                  </Link>
                </div>
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </div>
  );
};

export default NotificationMenu;
